import { Link } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img src="/apple-touch-icon.png" alt="EightyThreeLabs" className="w-8 h-8" />
              <span className="text-xl font-semibold">
                <span className="text-brand-primary">EightyThree</span>
                <span className="text-black dark:text-white">Labs</span>
              </span>
            </Link>
            
            {/* Dark Mode Toggle */}
            <DarkModeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 shadow-xl">
          <Link 
            to="/" 
            className="inline-flex items-center text-brand-primary hover:text-green-700 dark:hover:text-green-400 mb-8 transition-colors"
          >
            ← Back to Home
          </Link>
          
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 text-center">Privacy Policy</h1>
          <div className="text-center text-gray-600 dark:text-gray-400 mb-8 italic">
            Effective Date: January 1, 2025
          </div>
          
          <div className="prose max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Introduction</h2>
              <p className="text-gray-700 dark:text-gray-300">
                EightyThreeLabs ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website eightythreelabs.com and use our brain training games.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li><strong>Account Information:</strong> When you create an account, we collect your email address and any optional profile information you provide</li>
                <li><strong>Game Progress:</strong> We store your game scores, completion times, streaks, and progress statistics</li>
                <li><strong>Newsletter Subscription:</strong> Email addresses provided for our newsletter subscription</li>
                <li><strong>Communications:</strong> Any messages or feedback you send to us</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li><strong>Usage Data:</strong> Information about how you use our games, including pages visited, time spent, and features used</li>
                <li><strong>Device Information:</strong> Browser type, operating system, screen resolution, and device identifiers</li>
                <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
                <li><strong>Local Storage:</strong> Game preferences and settings stored locally on your device</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">We use the collected information to:</p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Provide and maintain our brain training games</li>
                <li>Track your progress and maintain game statistics</li>
                <li>Send newsletter updates (if subscribed)</li>
                <li>Improve our games and user experience</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li><strong>Service Providers:</strong> We may share information with trusted third-party services that help us operate our platform (such as hosting providers and analytics services)</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal requests</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, sale, or asset transfer, user information may be transferred as part of the business assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Advertising and Third-Party Services</h2>
              <p className="text-gray-700 dark:text-gray-300">
                Our website uses Google AdSense to display advertisements. Google AdSense may use cookies and web beacons to serve ads based on your visit to our site and other sites on the Internet. You can opt out of personalized advertising by visiting Google's <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-green-700 dark:hover:text-green-400 transition-colors">Ads Settings</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Remember your preferences and game settings</li>
                <li>Analyze website usage and performance</li>
                <li>Provide personalized content and advertisements</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                You can control cookies through your browser settings, though some features may not work properly if cookies are disabled.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Data Security</h2>
              <p className="text-gray-700 dark:text-gray-300">
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Your Rights</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">You have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Withdraw consent for data processing (where applicable)</li>
                <li>Unsubscribe from our newsletter at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Contact Us</h2>
              <p className="text-gray-700 dark:text-gray-300">
                If you have any questions about this Privacy Policy or our data practices, please contact us through our website at <Link to="/" className="text-brand-primary hover:text-green-700 dark:hover:text-green-400 transition-colors">eightythreelabs.com</Link>.
              </p>
            </section>
          </div>

          <hr className="my-8 border-gray-200 dark:border-gray-700" />
          
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
            © 2025 EightyThreeLabs. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;